import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RANK_NAMES, RANK_ORDER, formatDate } from '@pk/shared';
import { useCharacterSearch, useHouse, useTitleIndex, useWorld } from '../api/hooks';
import { Badge, Empty, ErrorBox, Loading, PageHeader, Panel, Screen, ScreenTop, ScrollArea, Segmented } from '../components/ui';
import { HouseBadge } from '../components/game';
import { Heraldry } from '../components/Heraldry';
import { IconChevron, IconSearch, IconUsers } from '../components/icons';

/** Static "places": features reachable by typing a word for them (label is matched too). */
const PLACES: { id: string; to: string; words: string }[] = [
  { id: 'family', to: '/?view=family', words: 'family members children heir spouse marry marriage education' },
  { id: 'chivalry', to: '/?view=family&filter=chivalry', words: 'knights squires squire knight dubbing guard captain chivalry' },
  { id: 'lands', to: '/map', words: 'lands council holdings titles build building barony county duchy vassal' },
  { id: 'mail', to: '/court', words: 'letters proposals inbox court write mail petition' },
  { id: 'pacts', to: '/court?tab=relations', words: 'alliances pacts allies diplomacy non-aggression relations' },
  { id: 'favours', to: '/court?tab=letters', words: 'favours favors hooks wardships wardship ward foster' },
  { id: 'factions', to: '/court?tab=factions', words: 'factions rebels independence claimant' },
  { id: 'markets', to: '/trade', words: 'markets market trade buy sell goods economy city prices orders tariff' },
  { id: 'guilds', to: '/trade?view=guilds', words: 'guilds guild rank journeyman master guildmaster xp mayor election' },
  { id: 'companies', to: '/trade?view=companies', words: 'companies company licence found workshop' },
  { id: 'caravans', to: '/trade?view=caravans', words: 'caravans caravan deliveries delivery goods road' },
  { id: 'loans', to: '/trade?view=loans', words: 'loans loan debt lend borrow repay bank gold' },
  { id: 'armies', to: '/map?view=military', words: 'armies army war wars levies troops military battle peace' },
  { id: 'schemes', to: '/court?tab=intrigue', words: 'schemes scheme plots plot intrigue murder abduct spy' },
  { id: 'map', to: '/map', words: 'map world realm kingdoms' },
  { id: 'chat', to: '/chat', words: 'chat messages dm channel' },
  { id: 'tree', to: '/tree/', words: 'tree family tree pedigree dynasty' },
  { id: 'chronicle', to: '/chronicle', words: 'chronicle history events log' },
  { id: 'settings', to: '/settings', words: 'settings options theme notifications push account' },
];
import '../styles/screens-people.css';

type Alive = 'any' | 'alive' | 'dead';

export function SearchScreen() {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');
  const [alive, setAlive] = useState<Alive>('any');

  // Debounce the submitted query so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(id);
  }, [input]);

  const results = useCharacterSearch(
    { q, alive: alive === 'any' ? undefined : alive === 'alive' },
    q.length > 0,
  );
  const hits = results.data ?? [];
  const house = useHouse();
  const ql = q.toLowerCase();
  const places = ql.length < 2 ? [] : PLACES.filter((p) => t(`places.${p.id}`).toLowerCase().includes(ql) || p.words.split(' ').some((w) => w.startsWith(ql) || (ql.length > 3 && ql.startsWith(w))))
    .map((p) => ({ ...p, to: p.id === 'tree' ? `/tree/${house.data?.house.id ?? ''}` : p.to }));

  return (
    <Screen>
      <ScreenTop>
      <PageHeader icon={<IconSearch />} title={t('search.title')} subtitle={t('search.sub')} />

      <Panel className="search-panel">
        <div className="search-box">
          <IconSearch />
          <input
            className="input"
            type="search"
            placeholder={t('search.placeholder')}
            value={input}
            maxLength={64}
            autoFocus
            onChange={(e) => setInput(e.target.value)}
            aria-label={t('search.title')}
          />
        </div>
        <div style={{ height: 10 }} />
        <Segmented<Alive> value={alive} onChange={setAlive} options={[
          { value: 'any', label: t('search.any') }, { value: 'alive', label: t('search.alive') }, { value: 'dead', label: t('search.dead') },
        ]} />
      </Panel>
      </ScreenTop>

      <ScrollArea>
      {places.length > 0 && (
        <Panel className="search-places" title={t('places.title')}>
          <ul className="list">
            {places.map((p) => (
              <li key={p.id}>
                <Link className="places-row" to={p.to}><b>{t(`places.${p.id}`)}</b><IconChevron /></Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {q.length === 0 && <div className="search-start"><IconUsers /><p>{t('search.start')}</p></div>}
      {results.isLoading && q.length > 0 && <Loading />}
      {results.isError && <ErrorBox error={results.error} onRetry={() => results.refetch()} />}
      {!results.isLoading && !results.isError && q.length > 0 && hits.length === 0 && <Empty>{t('search.empty')}</Empty>}
      {hits.length > 0 && <p className="search-count" role="status">{t('search.found', { count: hits.length })}</p>}

      <div className="sr-list">
        {hits.map((n) => <SearchRow key={n.id} id={n.id} name={n.titledName ?? n.name} houseId={n.houseId} houseName={n.houseName}
          birthDay={n.birthDay} deathDay={n.deathDay} />)}
      </div>
      </ScrollArea>
    </Screen>
  );
}

function SearchRow({ id, name, houseId, houseName, birthDay, deathDay }: {
  id: string; name: string; houseId: string | null; houseName: string | null; birthDay: number; deathDay: number | null;
}) {
  const { t } = useTranslation();
  const titles = useTitleIndex();
  const held = [...titles.values()]
    .filter((title) => title.holderId === id)
    .sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank])[0];
  const dead = deathDay !== null;

  return (
    <div className={`sr-card list-row ${dead ? 'dead' : ''}`}>
      <span className="sr-ico" aria-hidden="true">{houseId ? <HouseIcon houseId={houseId} /> : <IconUsers />}</span>
      <div className="sr-main">
        <Link to={`/character/${id}`} className="member-name link" style={{ borderBottom: 0 }}>{name}</Link>
        <div className="sr-dates">
          {formatDate(birthDay)} – {dead ? formatDate(deathDay) : t('search.present')}
        </div>
        <div className="sr-tags">
          {dead && <Badge tone="red">{t('search.deadTag')}</Badge>}
          {held && <Badge tone="gold"><Link className="link" to={`/title/${held.id}`}>{held.name} · {RANK_NAMES[held.rank].title}</Link></Badge>}
          {houseId && <Link className="link" to={`/tree/${houseId}`}><HouseBadge houseId={houseId} name={houseName} /></Link>}
        </div>
      </div>
      <Link className="sr-go" to={`/character/${id}`} aria-label={name}><IconChevron /></Link>
    </div>
  );
}

function HouseIcon({ houseId }: { houseId: string }) {
  const { data } = useWorld();
  const h = data?.houses.find((x) => x.id === houseId);
  return h ? <Heraldry seed={h.heraldrySeed} size={34} /> : <IconUsers />;
}
